import { DropdownPopover, ModalWindow, useDeviceType } from '@cian/ui-kit';
import { IconActionMore16 } from '@cian/ui-kit-design-tokens/icons';

import { type ReactNode, type SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CustomRoundButton } from '@/packages/shared-ui';
import { stopPropagation } from '@/packages/utils/event';

import { GalleryActions } from '../components/GalleryAcitons';
import { useListingScrollEffect } from '../hooks/useListingScrollEffect';

import { GalleryActionsMenuContainer } from './GalleryActionsMenu';

export function MoreActionsMenuButtonContainer(): ReactNode {
  const deviceType = useDeviceType();
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [isMenuOpened, setIsMenuOpened] = useState(false);

  const toggleMenu = useCallback((e: SyntheticEvent): void => {
    stopPropagation(e);
    setIsMenuOpened((opened: boolean) => !opened);
  }, []);

  const closeMenu = useCallback((): void => {
    setIsMenuOpened(false);
  }, []);

  const button = useMemo(
    () => (
      <CustomRoundButton
        data-testid="MoreControl"
        icon={<IconActionMore16 color="current_color" />}
        title="Подробнее"
        onClick={toggleMenu}
      />
    ),
    [toggleMenu],
  );

  useListingScrollEffect({ action: closeMenu, delta: 20, enabled: isMenuOpened });

  useEffect(() => {
    const handleBackdropClick = (e: MouseEvent): void => {
      if (tooltipRef.current !== null && !tooltipRef.current.contains(e.target as Node)) {
        e.stopPropagation();
        e.preventDefault();
        closeMenu();
      }
    };

    if (deviceType === 'desktop') {
      if (isMenuOpened) {
        document.addEventListener('click', handleBackdropClick, { capture: true });
      } else {
        document.removeEventListener('click', handleBackdropClick);
      }
    } else {
      document.removeEventListener('click', handleBackdropClick);
    }

    return (): void => {
      document.removeEventListener('click', handleBackdropClick);
    };
  }, [isMenuOpened, deviceType, closeMenu]);

  if (deviceType === 'desktop') {
    return (
      <DropdownPopover
        content={
          <GalleryActions.Dropdown ref={tooltipRef}>
            <GalleryActionsMenuContainer onItemClick={closeMenu} />
          </GalleryActions.Dropdown>
        }
        open={isMenuOpened}
      >
        {button}
      </DropdownPopover>
    );
  }

  return (
    <>
      {button}
      <ModalWindow
        content={<GalleryActionsMenuContainer onItemClick={closeMenu} />}
        header="Действия"
        open={isMenuOpened}
        onClose={closeMenu}
      />
    </>
  );
}
